import SubCategoryCard from '../../../components/cards/SubCategoryCard';
import {
    useFetchSubCategories,
    useFetchSubCategory,
    useFetchUnlockedDemoLectures,
} from '../../../lib/fetches';
import CategoriesTabs from '../../../components/tabs/CategoriesTabs.tsx';
import { useAuth } from '../../../lib/auth';
import { useNavigate, useParams } from 'react-router';
import AuthLayout from '../../layouts/AuthLayout';
import Skeleton, {
    SkeletonSubCategoryCard,
} from '../../../components/Skeleton';
import clsx from 'clsx';
import { EmptyState } from '../../../components/emptyStates/EmptyState.tsx';
import { Lecture, LectureCategory } from '../../../lib/types.ts';
import { Button } from '../../../components/buttons/Button.tsx';
import CategoryCard from '../../../components/cards/CategoryCard.tsx';
import Card from '../../../components/cards/Card.tsx';
import { Video } from '../../../components/onboarding/Video.tsx';
import {
    UilBriefcase,
    UilPentagon,
    UilUsersAlt,
    UilVideo,
} from '@iconscout/react-unicons';
import VideoCard from '../../../components/cards/VideoCard.tsx';
import { useMemo, useState } from 'react';
import { parseTime } from '../../../lib/helpers.ts';
import MethodologyDialog from '../../../components/dialogs/MethodologyDialog.tsx';

interface SubCategoryListProps {
    items: LectureCategory[] | undefined;
    isLoading: boolean;
    showBadges: boolean;
}

function SubCategoryList({
    items,
    isLoading,
    showBadges,
}: SubCategoryListProps) {
    return (
        <>
            {isLoading ? (
                <SkeletonSubCategoryCard count={8} className="mb-8" />
            ) : (
                <>
                    {items?.length ? (
                        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                            {items?.map((lectureCategory) => (
                                <SubCategoryCard
                                    key={lectureCategory.slug}
                                    lectureCategory={lectureCategory}
                                    showBadge={showBadges}
                                />
                            ))}
                        </div>
                    ) : (
                        <></>
                    )}
                </>
            )}
        </>
    );
}

export default function IntroductionPage() {
    useAuth({
        middleware: 'auth',
        redirectTo: '/',
        tmp: 'CategoryPage',
    });

    const navigate = useNavigate();
    // const { category = 'vse' } = useParams();

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    // const { data: subCategories, isLoading } = useFetchSubCategories(category!);

    const { data: category } = useFetchSubCategory('onboarding');

    const { data: unlockedVideos } = useFetchUnlockedDemoLectures();
    const [isMethodologyDialogOpen, setIsMethodologyDialogOpen] = useState(false);
    const [selectedLecture, setSelectedLecture] = useState<Lecture | null>(null);

    const videos = useMemo(() => {
        if (!category) {
            return {};
        }

        const videos: Record<string, Lecture & { length: string }> = {};

        category.lectures?.forEach((lecture) => {
            const { minutes, seconds } = parseTime(lecture.video?.length);

            videos[lecture.slug] = {
                ...lecture,
                length: `${minutes > 0 ? `${minutes} min` : ''}${
                    seconds > 0 ? ` ${seconds} s` : ''
                }`,
            };
        });

        return videos;
    }, [category]);

    if (!category) {
        return <Skeleton className="h-screen w-screen" />;
    }

    return (
        <AuthLayout className="bg-white">
            <main
                className={clsx(
                    'mx-auto box-content max-w-6xl py-9',
                    'px-4 sm:px-8 md:px-10'
                )}
            >
                <div className="flex flex-col space-y-8">
                    <div className="grid grid-cols-4 items-end gap-4">
                        <div className="col-span-3">
                            <h1>Začínáme se Všímavcem</h1>
                            <p className="text-lg text-ink-800">
                                Pomůžeme vám seznámit se s aplikací a vybrat si
                                cvičení, která jsou pro vaši třídu ta pravá.
                            </p>
                        </div>
                        <div>
                            <Button
                                color="product"
                                className="w-full"
                                onClick={() => {
                                    navigate('/onboarding?step=intro-video&ref=zaciname');
                                }}
                            >
                                Spustit průvodce
                            </Button>
                        </div>
                    </div>

                    <Card color="blue">
                        <div className="grid grid-cols-3">
                            <h3 className="tracking-wide">
                                <span className="text-gray-500">
                                    Úvodní video:
                                </span>{' '}
                                Proč zařadit Všímavce?
                            </h3>
                            <div className="col-span-2">
                                <Video
                                    slug="uvitaci-video"
                                    category={category}
                                />
                            </div>
                        </div>
                    </Card>

                    <Card>
                        <h3 className="flex items-center gap-2 pb-4 text-2xl tracking-wide">
                            <UilUsersAlt size={32} /> Jak pracovat se Všímavcem
                            dle role
                        </h3>

                        <div className="grid w-full grid-cols-3 gap-4">
                            <VideoCard
                                color="orange"
                                heading="Vedení školy"
                                subheading={
                                    videos['vedeni-skoly']?.length ?? ''
                                }
                                video={
                                    <Video
                                        className="flex content-end"
                                        slug="vedeni-skoly"
                                        category={category}
                                    />
                                }
                            />
                            <VideoCard
                                color="green"
                                heading="Pracovník ŠPP"
                                subheading={
                                    videos['pracovnik-spp']?.length ?? ''
                                }
                                video={
                                    <Video
                                        className="flex content-end"
                                        slug="pracovnik-spp"
                                        category={category}
                                    />
                                }
                            />
                            <VideoCard
                                color="purple"
                                heading="Pedagogický pracovník"
                                subheading={
                                    videos['pedagogicky-pracovnik']?.length ??
                                    ''
                                }
                                video={
                                    <Video
                                        className="flex content-end"
                                        slug="pedagogicky-pracovnik"
                                        category={category}
                                    />
                                }
                            />
                        </div>
                    </Card>

                    <Card>
                        <h3 className="flex items-center gap-2 pb-4 text-2xl tracking-wide">
                            <UilBriefcase size={32} /> Jak pracovat se Všímavcem
                            dle věkové skupiny
                        </h3>

                        <div className="grid w-full grid-cols-3 gap-4">
                            <VideoCard
                                color="red"
                                heading="Mateřská škola"
                                subheading={
                                    videos['materska-skola']?.length ?? ''
                                }
                                video={
                                    <Video
                                        className="flex content-end"
                                        slug="vedeni-skoly"
                                        category={category}
                                    />
                                }
                            />
                            <VideoCard
                                color="orange"
                                heading="1. – 3. třída"
                                subheading={videos['1-3-trida']?.length ?? ''}
                                video={
                                    <Video
                                        className="flex content-end"
                                        slug="pracovnik-spp"
                                        category={category}
                                    />
                                }
                            />
                            <VideoCard
                                color="blue"
                                heading="4. – 5. třída"
                                subheading={videos['4-5-trida']?.length ?? ''}
                                video={
                                    <Video
                                        className="flex content-end"
                                        slug="pedagogicky-pracovnik"
                                        category={category}
                                    />
                                }
                            />
                        </div>
                    </Card>

                    <Card>
                        <h3 className="flex items-center gap-2 text-2xl tracking-wide pb-4">
                            <UilPentagon size={32} /> Představení Všímavce pro
                            děti
                        </h3>

                        <div className="grid w-full grid-cols-3 gap-4">
                            <VideoCard
                                color="red"
                                heading="Mateřská škola"
                                subheading={
                                    videos['uvitani-deti-ms']?.length ?? ''
                                }
                                video={
                                    <Video
                                        className="flex content-end"
                                        slug="uvitani-deti-ms"
                                        category={category}
                                    />
                                }
                            />
                            <VideoCard
                                color="orange"
                                heading="1. – 3. třída"
                                subheading={videos['uvitani-deti-1-3-trida']?.length ?? ''}
                                video={
                                    <Video
                                        className="flex content-end"
                                        slug="uvitani-deti-1-3-trida"
                                        category={category}
                                    />
                                }
                            />
                            <VideoCard
                                color="blue"
                                heading="4. – 5. třída"
                                subheading={videos['uvitani-deti-4-5-trida']?.length ?? ''}
                                video={
                                    <Video
                                        className="flex content-end"
                                        slug="uvitani-deti-4-5-trida"
                                        category={category}
                                    />
                                }
                            />
                        </div>
                    </Card>

                    <Card color="blue">
                        <h3 className="flex items-center gap-2 pb-4 text-2xl tracking-wide">
                            <UilVideo size={32} /> Videa v DEMO verzi
                        </h3>

                        <div className="grid w-full grid-cols-3 gap-4">
                            {unlockedVideos?.map((video) => {
                                const { minutes, seconds } = parseTime(
                                    video.video?.length
                                );
                                const subheading = `${
                                    minutes > 0 ? `${minutes} min` : ''
                                }${seconds > 0 ? ` ${seconds} s` : ''}`;
                                return (
                                    <VideoCard
                                        key={video.slug}
                                        color="white"
                                        heading={video.name}
                                        subheading={subheading}
                                        video={
                                            <Video
                                                className="flex content-end"
                                                slug={video.slug}
                                                category={{
                                                    lectures: [video],
                                                } as LectureCategory}
                                            />
                                        }
                                        withButton={() => {
                                            setSelectedLecture(video);
                                            setIsMethodologyDialogOpen(true);
                                        }}
                                    />
                                );
                            })}
                        </div>
                    </Card>
                </div>

                <MethodologyDialog
                    isOpen={isMethodologyDialogOpen}
                    onClose={() => {
                        setIsMethodologyDialogOpen(false);
                    }}
                    heading={selectedLecture?.name ?? ''}
                    content={selectedLecture?.methodology ?? null}
                />
            </main>
        </AuthLayout>
    );
}
